import React, { useState, useEffect } from 'react'
import { Offcanvas, Button, Container, Dropdown, Row, Col, Tabs, Tab, ListGroup, Stack, Badge, Form, Spinner } from 'react-bootstrap'
import { InputFilter, CustomOffcanvas, InputCheck, InputField, InputSelect } from '../'
import { getAzureGroups, fetchPreview, fetchSchemaExtensions, fetchEvents } from '../../utils/requestHelper'
import { splitDynamicFilterQuery, splitDynamicFilterGroup } from '../../utils/queryHelper'
import { customArrayFilter } from '../../utils/filterHelper'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faTrashAlt, faPlus } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import IconButton from '../iconButton'
import { useModal } from '../../hooks/useModal'
import { useWindowDimensions } from '../../hooks/useWindowDimensions'
import SelectDirectoryObjectModal from './selectDirectoryObjectModal'

const AUDIT_PAGE_SIZE = 50
const AUDIT_PERIODS = [1, 7, 30, 60]

const SynchronisationModal = (props) => {
  const { t } = useTranslation()
  const { width } = useWindowDimensions()
  const [tab, setTab] = useState('settings');
  const [source, setSource] = useState(null)
  const [target, setTarget] = useState(null)
  const [previewUsers, setPreviewUsers] = useState()
  const [schemaExtensions, setschemaExtensions] = useState([])
  const [filterBuilder, setFilterBuilder] = useState(true)
  const [auditDays, setAuditDays] = useState(7)
  const [auditLog, setAuditLog] = useState(null)
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditError, setAuditError] = useState(false)
  const taskId = props.selected?._id

  const [modalOpenExclude, setModalOpenExclude, modalEditExclude, setModalEditExclude] = useModal()
  const [modalOpenInclude, setModalOpenInclude, modalEditInclude, setModalEditInclude] = useModal()

  useEffect(() => {
    if (props.selected) {
      const fetchData = async () => {
        var result = await fetchSchemaExtensions()
        if (result.success === true)
          setschemaExtensions(result.data)
      }
      setSource(props.selected.source)
      setTarget(props.selected.target)
      fetchData()
    }
  }, [props.selected])

  useEffect(() => {
    if (props.modalOpen === false) {
      setTab('settings')
      setFilterBuilder(true)
      setAuditDays(7)
    }
  }, [props.modalOpen])

  useEffect(() => {
    if (tab !== 'auditlog' || !taskId) {
      setAuditLog(null)
      return
    }
    let cancelled = false
    setAuditLoading(true)
    setAuditError(false)
    fetchEvents(taskId, { from: moment().subtract(auditDays, 'days').toISOString(), limit: AUDIT_PAGE_SIZE })
      .then((result) => { if (!cancelled) setAuditLog(result) })
      .catch(() => { if (!cancelled) setAuditError(true) })
      .finally(() => { if (!cancelled) setAuditLoading(false) })
    return () => { cancelled = true }
  }, [tab, auditDays, taskId])

  useEffect(() => {
    const fetchData = async (data) => {
      var result = await fetchPreview(data)
      if (result.success === true) {
        var filteredArr = [].concat(result.result)
        if (props.selected.whitelistFilter)
          props.selected.whitelistFilter.forEach(element => {
            filteredArr.push({ id: element.id, displayName: element.displayName, userType: 'Included' })
          });
        filteredArr = filteredArr.reduce((acc, current) => {
          const x = acc.find(item => item.id === current.id);
          if (!x) {
            return acc.concat([current]);
          } else {
            return acc;
          }
        }, []);
        setPreviewUsers({ nextLink: result.nextLink, result: filteredArr, success: result.success })
      }
    }
    if (tab === 'preview' && source) {
      fetchData(source)
    }
    if (tab !== 'preview')
      setPreviewUsers(null)
  }, [tab])

  const style = {
    bottom: {
      bottom: 0,
      position: 'absolute',
      background: 'white',
      width: '100%',
      padding: 25
    }
  }
  
  if (props.modalOpen === false || !props.modalOpen)
    return null

  const handleOnClickNextLink = async () => {
    var result = await fetchPreview(source, previewUsers.nextLink)
    if (result.success === true) {
      var array = previewUsers.result.concat(result.result)
      if (props.selected.whitelistFilter)
        props.selected.whitelistFilter.forEach(element => {
          array.push({ id: element.id, displayName: element.displayName, userType: 'Included' })
        });
      const filteredArr = array.reduce((acc, current) => {
        const x = acc.find(item => item.id === current.id);
        if (!x) {
          return acc.concat([current]);
        } else {
          return acc;
        }
      }, []);
      setPreviewUsers({ nextLink: result.nextLink, result: filteredArr, success: result.success })
    }
  }

  const handleOnClickAddUser = (e) => {
    switch (e) {
      case 'include':
        setModalOpenInclude(true)
        break;
      case 'exclude':
        setModalOpenExclude(true)
        break;
      default:
        console.warn(`Unhandled case: ${e}`);
        break;
    }
  }

  const handleOnSaveAddUser = (e) => {
    const { controlId } = e
    props.onSelectUserAdd(e)
    switch (controlId) {
      case 'whitelistFilter':
        setModalOpenInclude(false)
        break;
      case 'blacklistFilter':
        setModalOpenExclude(false)
        break;
      default:
        console.warn(`Unhandled controlId: ${controlId}`);
        break;
    }
  }

  const handleOnClickAuditLogMore = async () => {
    setAuditLoading(true)
    try {
      const result = await fetchEvents(taskId, {
        from: moment().subtract(auditDays, 'days').toISOString(),
        limit: AUDIT_PAGE_SIZE,
        skip: auditLog.items.length
      })
      // New events shift the paging window, so drop duplicates.
      const knownIds = new Set(auditLog.items.map((item) => item._id))
      setAuditLog({ ...result, items: auditLog.items.concat(result.items.filter((item) => !knownIds.has(item._id))) })
    } catch {
      setAuditError(true)
    } finally {
      setAuditLoading(false)
    }
  }

  const renderAuditLogItem = (event) => {
    const { meta = {}, payload = {} } = event.data || {}
    const failed = event.type.endsWith('_FAILED')
    return (
      <ListGroup.Item key={event._id}>
        <Row>
          <Col md={3} lg={3} xs={12}>
            {moment(meta.timestamp).format('YYYY-MM-DD HH:mm:ss')}
          </Col>
          <Col md={4} lg={4} xs={12}>
            <div>{payload.userDisplayName || payload.userId}</div>
            {payload.userPrincipalName && <small className="text-muted">{payload.userPrincipalName}</small>}
          </Col>
          <Col md={2} lg={2} xs={6}>
            <Badge bg={payload.action === 'add' ? 'primary' : 'secondary'}>
              {t(`auditlog-action-${payload.action}-${payload.role || 'member'}`)}
            </Badge>
            {payload.reason && <div><small className="text-muted">{t(`auditlog-reason-${payload.reason}`)}</small></div>}
          </Col>
          <Col md={3} lg={3} xs={6}>
            <Badge bg={failed ? 'danger' : 'success'}>
              {t(failed ? 'auditlog-status-failed' : 'auditlog-status-success')}
            </Badge>
            {failed && payload.error && <div><small className="text-danger">{payload.error}</small></div>}
          </Col>
        </Row>
      </ListGroup.Item>
    )
  }

  const getPreviewUsersStyle = (id) => {
    var isIncluded = props.selected.whitelistFilter && customArrayFilter(props.selected.whitelistFilter, 'id', id)
    if (isIncluded)
      return { color: '#75b798' }
    var isExcluded = props.selected.blacklistFilter && customArrayFilter(props.selected.blacklistFilter, 'id', id)
    if (isExcluded)
      return { color: '#ea868f', textDecoration: 'line-through' }
  }

  const renderExtendedFilterGroup = (filterGroups) => {
    var filterGroupsArray = splitDynamicFilterGroup(filterGroups)
    return (
      <div>
        {filterGroupsArray.map((element, index) => {
          return renderExtendedFilter(element, index)
        })}
      </div>
    )
  }

  const renderExtendedFilter = (filter, filterGroupIndex) => {
    var array = splitDynamicFilterQuery(filter)
    return (
      <div style={{ backgroundColor: '#eff5ff', padding: 10, marginBottom: 10 }} key={`dynamic-filtergroup-${filterGroupIndex}`}>
        {array.map((element, index) => {
          return (
            <InputFilter
              key={`extendedFilter-${index}`}
              index={index}
              value={element}
              actions={props.actions}
              filterGroupIndex={filterGroupIndex}
              schemaExtensions={schemaExtensions}
              onSave={props.onChangeFilter}
              onDelete={props.onDeleteFilter}
            />
          )
        })}
        {props.edit &&
          <Button
            variant="link"
            onClick={() => props.onAddFilter(filterGroupIndex)}
          >
            {t('button-addFilter')}
          </Button>
        }
      </div>
    )
  }

  return (
    <CustomOffcanvas
      show={props.modalOpen}
      onModalClose={() => props.onModalClose(false)}
      width={width < 720 ? width : width * (60 / 100)}
      headline={target ? target.displayName : t('synchronisation-modal-headline')}
    >
      <Offcanvas.Body style={{ fontSize: "1.0rem" }}>
        <Container>
          <Tabs
            id="controlled-tab-example"
            activeKey={tab}
            onSelect={(k) => setTab(k)}
            className="mb-3"
          >
            <Tab eventKey="settings" title={t('synchronisation-modal-basic')}>
              <Row className='mt-25'>
                <Col md={12} lg={12} xs={12} style={{ textAlign: "right" }}>
                  {props.actions.includes('modify_tasks') &&
                    <IconButton icon={faEdit} color={'#646464'} tooltip={t('tooltip-edit')} id={'modal-edit'} key={'modal-edit'} placement={'top'} onClick={() => props.setEdit(!props.edit)} />
                  }
                </Col>
                <Col md={12} lg={12}>
                  <InputSelect
                    key={`input-filter-target`}
                    label={t('input-label-target')}
                    placeholder={t('placeholder-label-target')}
                    controlId='target'
                    onPremisesSyncDisabled
                    options={getAzureGroups}
                    edit={props.edit}
                    handleOnChange={props.onSelectChange}
                    value={target}
                    filter={{ onPremisesSyncEnabled: 'null' }}
                  />
                </Col>
                <Col md={12} lg={12}>
                  <InputField
                    label={t('input-label-description')}
                    controlId='description'
                    type='textarea'
                    value={props.selected.description}
                    edit={props.edit}
                    handleOnChange={props.onInputChange}
                  />
                </Col>
                <Col md={12} lg={12}>
                  <InputCheck
                    type="switch"
                    controlId='extendedFilter'
                    label={t('input-label-extendedFilter')}
                    value={t(`selected-label-${props.selected.extendedFilter}`)}
                    checked={props.selected.extendedFilter}
                    edit={props.edit}
                    handleOnChange={props.onInputChange}
                  />
                </Col>
                {!props.selected.extendedFilter ? (
                  <Col md={12} lg={12}>
                    <InputSelect
                      key={`input-select-source`}
                      label={t('input-label-source')}
                      placeholder={t('placeholder-label-source')}
                      controlId='source'
                      options={getAzureGroups}
                      edit={props.edit}
                      handleOnChange={props.onSelectChange}
                      value={source}
                    />
                  </Col>
                ) : (
                  <>
                    <Stack direction="horizontal" gap={1}>
                      <label className={'p-2'}>{t(`label-filter`)}</label>
                      <div className={"p-2 ms-auto"}>
                        <Button
                          variant={filterBuilder ? "primary" : "outline-primary"}
                          size="sm"
                          onClick={() => setFilterBuilder(true)}
                        >
                          {t('button-viewBuilder')}
                        </Button>
                      </div>
                      <div className={"p-2"}>
                        <Button
                          variant={!filterBuilder ? "primary" : "outline-primary"}
                          size="sm"
                          onClick={() => setFilterBuilder(false)}
                        >
                          {t('button-viewCode')}
                        </Button>
                      </div>
                    </Stack>
                    {filterBuilder && props.selected.extendedFilter && props.selected.source && renderExtendedFilterGroup(props.selected.source.id)}
                    {filterBuilder && props.edit &&
                      <Col md={12} lg={12} xs={12} className="mt-25" style={{ textAlign: "left" }}>
                        <Dropdown onSelect={props.onAddFilterGroup}>
                          <Dropdown.Toggle variant="outline-primary" id="dropdown-basic">
                            {t('button-addFilterGroup')}
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item eventKey="OR">OR</Dropdown.Item>
                            <Dropdown.Item eventKey="AND">AND</Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </Col>
                    }
                    {props.selected.extendedFilter && props.selected.source && !filterBuilder &&
                      <Col md={12} lg={12}>
                        <InputField
                          label={null}
                          controlId='filter'
                          type='textarea'
                          value={props.selected.source.id}
                          edit={props.edit}
                          handleOnChange={props.onReplaceFilter}
                        />
                      </Col>
                    }
                  </>
                )}
              </Row>
            </Tab>
            {taskId && <Tab eventKey="auditlog" title={t('synchronisation-modal-auditlog')}>
              <Row className='mt-25 align-items-end'>
                <Col md={6} lg={4} xs={12}>
                  <Form.Label htmlFor="auditlog-period">{t('auditlog-period')}</Form.Label>
                  <Form.Select id="auditlog-period" value={auditDays} onChange={(e) => setAuditDays(Number(e.target.value))}>
                    {AUDIT_PERIODS.map((days) => (
                      <option key={days} value={days}>{t('auditlog-period-days', { count: days })}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={6} lg={8} xs={12} style={{ textAlign: "right" }}>
                  {auditLog && <span className="text-muted">{t('auditlog-total', { count: auditLog.total })}</span>}
                </Col>
              </Row>
              <Row className='mt-25' style={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: '55vh' }}>
                <Col md={12} lg={12} xs={12}>
                  <ListGroup variant="flush">
                    <ListGroup.Item key={`listGroup-auditlog-header`}>
                      <Row>
                        <Col md={3} lg={3} xs={12}><b>{t('auditlog-timestamp')}</b></Col>
                        <Col md={4} lg={4} xs={12}><b>{t('auditlog-user')}</b></Col>
                        <Col md={2} lg={2} xs={6}><b>{t('auditlog-action')}</b></Col>
                        <Col md={3} lg={3} xs={6}><b>{t('auditlog-status')}</b></Col>
                      </Row>
                    </ListGroup.Item>
                    {auditLog && auditLog.items.map(renderAuditLogItem)}
                    {auditLog && auditLog.items.length === 0 && !auditLoading && (
                      <ListGroup.Item key={`listGroup-auditlog-empty`} className="text-muted">{t('auditlog-empty')}</ListGroup.Item>
                    )}
                    {auditError && (
                      <ListGroup.Item key={`listGroup-auditlog-error`} className="text-danger">{t('auditlog-error')}</ListGroup.Item>
                    )}
                    {auditLoading && (
                      <ListGroup.Item key={`listGroup-auditlog-loading`} style={{ textAlign: "center" }}>
                        <Spinner animation="border" size="sm" />
                      </ListGroup.Item>
                    )}
                    {auditLog && !auditLoading && auditLog.items.length < auditLog.total && (
                      <ListGroup.Item key={`listGroup-auditlog-more`} style={{ textAlign: "center" }}>
                        <Button variant="outline-primary" onClick={handleOnClickAuditLogMore}>
                          {t('button-more')}
                        </Button>
                      </ListGroup.Item>
                    )}
                  </ListGroup>
                </Col>
              </Row>
            </Tab>}
            <Tab eventKey="blacklist" title={t('synchronisation-modal-blacklist')}>
              <Row className='mt-25'>
                <Col md={12} lg={12} xs={12} className="mt-25">
                  <ListGroup variant="flush">
                    <ListGroup.Item key={`listGroup-blacklist-header`}>
                      <Row>
                        <Col md={8} lg={8} xs={8} style={{ textAlign: "left" }}>
                          <b>{t('input-label-displayName')}</b>
                        </Col>
                        <Col md={4} lg={4} xs={4} style={{ textAlign: "right" }}>
                          <b>{t('input-label-delete')}</b>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                    {props.selected.blacklistFilter && props.selected.blacklistFilter.map((element) => (
                      <ListGroup.Item key={element.id}>
                        <Row>
                          <Col md={8} lg={8} xs={8} style={{ textAlign: "left" }}>
                            {element.displayName}
                            {' '}<Badge bg="secondary">{t(`directory-object-${element.type || 'user'}`)}</Badge>
                          </Col>
                          <Col md={4} lg={4} xs={4} style={{ textAlign: "right" }}>
                            {props.actions.includes('modify_tasks') &&
                              <IconButton icon={faTrashAlt} color={'#646464'} tooltip={t('tooltip-delete')} id={`IconButton-edit-${element.id}`} key={`IconButton-edit-${element.id}`} placement={'top'} onClick={() => props.onSelectUserRemove(element.id, 'blacklistFilter')} />
                            }
                          </Col>
                        </Row>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Col>
                <Col md={12} lg={12} xs={12} className="mt-25" style={{ textAlign: "left" }}>
                  {props.actions.includes('modify_tasks') &&
                    <Button
                      variant="outline-primary"
                      onClick={() => handleOnClickAddUser('exclude')}
                    >
                      <FontAwesomeIcon icon={faPlus} style={{ marginRight: 7 }} />
                      {t('button-addUser')}
                    </Button>
                  }
                </Col>
              </Row>
            </Tab>
            <Tab eventKey="whitelist" title={t('synchronisation-modal-whitelist')}>
              <Row className='mt-25'>
                <Col md={12} lg={12} xs={12} className="mt-25">
                  <ListGroup variant="flush">
                    <ListGroup.Item key={`listGroup-whitelist-header`}>
                      <Row>
                        <Col md={8} lg={8} xs={8} style={{ textAlign: "left" }}>
                          <b>{t('input-label-displayName')}</b>
                        </Col>
                        <Col md={4} lg={4} xs={4} style={{ textAlign: "right" }}>
                          <b>{t('input-label-delete')}</b>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                    {props.selected.whitelistFilter && props.selected.whitelistFilter.map((element) => (
                      <ListGroup.Item key={element.id}>
                        <Row>
                          <Col md={8} lg={8} xs={8} style={{ textAlign: "left" }}>
                            {element.displayName}
                            {' '}<Badge bg="secondary">{t(`directory-object-${element.type || 'user'}`)}</Badge>
                          </Col>
                          <Col md={4} lg={4} xs={4} style={{ textAlign: "right" }}>
                            {props.actions.includes('modify_tasks') &&
                              <IconButton icon={faTrashAlt} color={'#646464'} tooltip={t('tooltip-delete')} id={`IconButton-edit-${element.id}`} key={`IconButton-edit-${element.id}`} placement={'top'} onClick={() => props.onSelectUserRemove(element.id, 'whitelistFilter')} />
                            }
                          </Col>
                        </Row>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Col>
                <Col md={12} lg={12} xs={12} className="mt-25" style={{ textAlign: "left" }}>
                  {props.actions.includes('modify_tasks') &&
                    <Button
                      variant="outline-primary"
                      onClick={() => handleOnClickAddUser('include')}
                    >
                      <FontAwesomeIcon icon={faPlus} style={{ marginRight: 7 }} />
                      {t('button-addUser')}
                    </Button>
                  }
                </Col>
              </Row>
            </Tab>
            <Tab eventKey="preview" title={t('synchronisation-modal-preview')}>
              <Row className='mt-25' style={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: '60vh' }}>
                <Col md={12} lg={12} xs={12} className="mt-25">
                  <ListGroup variant="flush">
                    <ListGroup.Item key={`listGroup-preview-header`}>
                      <Row>
                        <Col md={6} lg={6} xs={12} style={{ textAlign: "left" }}>
                          <b>{t('input-label-displayName')}</b>
                        </Col>
                        <Col md={6} lg={6} xs={12} style={{ textAlign: "left" }}>
                          <b>{t('input-label-userType')}</b>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                    {previewUsers && previewUsers.result.map((element) => (
                      <ListGroup.Item key={element.id} style={getPreviewUsersStyle(element.id)}>
                        <Row>
                          <Col md={6} lg={6} xs={12} style={{ textAlign: "left" }}>
                            {element.displayName}
                          </Col>
                          <Col md={6} lg={6} xs={12} style={{ textAlign: "left" }}>
                            {element.userType}
                          </Col>
                        </Row>
                      </ListGroup.Item>
                    ))}
                    {previewUsers && previewUsers.nextLink && (
                      <ListGroup.Item key={`listGroup-preview-nextlink`}>
                        <Row>
                          <Col style={{ textAlign: "center" }}>
                            <Button
                              variant="outline-primary"
                              onClick={handleOnClickNextLink}
                            >
                              {t('button-more')}
                            </Button>
                          </Col>
                        </Row>
                      </ListGroup.Item>
                    )}
                  </ListGroup>
                </Col>
              </Row>
            </Tab>
          </Tabs>
          <SelectDirectoryObjectModal
            controlId='blacklistFilter'
            existingEntries={props.selected.blacklistFilter}
            edit={modalEditExclude}
            setEdit={setModalEditExclude}
            modalOpen={modalOpenExclude}
            onModalClose={setModalOpenExclude}
            onModalSave={handleOnSaveAddUser}
          />
          <SelectDirectoryObjectModal
            controlId='whitelistFilter'
            existingEntries={props.selected.whitelistFilter}
            edit={modalEditInclude}
            setEdit={setModalEditInclude}
            modalOpen={modalOpenInclude}
            onModalClose={setModalOpenInclude}
            onModalSave={handleOnSaveAddUser}
          />
        </Container>
      </Offcanvas.Body>
      <div style={style.bottom}>
        <Stack direction="horizontal" gap={3}>
          {props.actions.includes('delete_tasks') &&
            <Button variant="secondary" onClick={() => props.onModalRemove(false)}>{t('button-delete')}</Button>
          }
          <Button style={{ marginLeft: 'auto' }} variant="secondary" onClick={() => props.onModalClose(false)}>{t('button-close')}</Button>
          {props.actions.includes('modify_tasks') && <Button variant="primary" onClick={() => props.onModalSave(false)}>{t('button-save')}</Button>}
        </Stack>
      </div>
    </CustomOffcanvas>
  )
}

export default SynchronisationModal